import { isUndefined } from 'lodash';

import { OfferDiscountTypes } from '@fhs-legacy/frontend/src/enums/menu';
import { ISelectedOfferPersistedState } from '@fhs-legacy/frontend/src/state/offers/hooks/types';
import { dollarsToCents } from '@fhs-legacy/frontend/src/utils';
import { ICartEntry, IOfferDiscount } from '@rbi-ctg/menu';

import {
  ILoyaltyInfo,
  getItemLoyaltyRewardDiscount,
  getItemOfferDiscount,
  getLoyaltyOfferDiscount,
  isOfferDiscount,
} from '../loyalty-helpers';

interface ICartEntryForPricing extends Pick<ICartEntry, 'price' | 'quantity' | 'cartId'> {}

export function computeCartTotal(
  offerDetails: ISelectedOfferPersistedState,
  cartEntries: ICartEntry[],
  loyaltyInfo: ILoyaltyInfo
) {
  const { selectedOffer, selectedOfferCartEntry, selectedOfferPrice } = offerDetails;

  const cartEntriesWithOffer = buildCartEntriesWithOffer(
    cartEntries,
    selectedOffer,
    selectedOfferCartEntry,
    selectedOfferPrice
  );

  const baseTotal = calculateBaseTotal(cartEntriesWithOffer);
  const loyaltyDiscounts = calculateLoyaltyDiscounts(cartEntries, loyaltyInfo);
  const subtotalAfterLoyalty = Math.max(baseTotal - loyaltyDiscounts, 0);

  const offerDiscount = getOfferDiscount(selectedOffer, loyaltyInfo);

  if (!offerDiscount) {
    return subtotalAfterLoyalty;
  }

  return applyOfferDiscountWithDonationHandling(
    cartEntriesWithOffer,
    subtotalAfterLoyalty,
    offerDiscount
  );
}

// ---- COMPUTE CART TOTAL HELPERS -----
function buildCartEntriesWithOffer(
  cartEntries: ICartEntry[],
  selectedOffer: ISelectedOfferPersistedState['selectedOffer'],
  selectedOfferCartEntry: ISelectedOfferPersistedState['selectedOfferCartEntry'],
  selectedOfferPrice: number | undefined
): ICartEntry[] {
  // When the offer option is OfferDiscount we discount the entire total not change the price of the offer.
  const shouldAddOfferToCart = selectedOffer && isOfferDiscount(selectedOffer.option);

  if (!shouldAddOfferToCart) {
    return cartEntries;
  }

  return [
    ...cartEntries,
    {
      ...(selectedOfferCartEntry as ICartEntry),
      price: selectedOfferPrice || 0,
    },
  ];
}

export function calculateBaseTotal(cartEntries: ICartEntry[]): number {
  const reduceCartEntryChildren = (
    accumulatedPrice: number,
    { children = [], price = 0 }: ICartEntry
  ): number => accumulatedPrice + children.reduce(reduceCartEntryChildren, price);

  const priceForCartEntry = (item: ICartEntry): number =>
    (item.children || []).reduce(reduceCartEntryChildren, 0) * (item.quantity || 1) +
    (item.price || 0);
  return cartEntries.reduce((total, entry) => total + priceForCartEntry(entry), 0);
}

function getOfferDiscount(selectedOffer: any, loyaltyInfo: ILoyaltyInfo) {
  if (isOfferDiscount(selectedOffer?.option)) {
    return selectedOffer.option;
  }

  return getLoyaltyOfferDiscount(loyaltyInfo);
}

function applyOfferDiscountWithDonationHandling(
  cartEntries: ICartEntry[],
  subtotal: number,
  offerDiscount: IOfferDiscount
): number {
  const donationItem = cartEntries.find(item => item.isDonation);

  if (donationItem?.price) {
    const subtotalWithoutDonation = subtotal - donationItem.price;
    const discountedSubtotal = computeTotalWithOfferDiscount(
      subtotalWithoutDonation,
      offerDiscount
    );
    return discountedSubtotal + donationItem.price;
  }

  return computeTotalWithOfferDiscount(subtotal, offerDiscount);
}

function computeTotalWithOfferDiscount(totalCents: number, offerDiscount?: IOfferDiscount) {
  const { discountValue, discountType } = offerDiscount ?? {};

  // We can't apply a discount if we don't have a discountValue
  if (!discountValue || discountValue === 0) {
    return totalCents;
  }

  if (discountType === OfferDiscountTypes.AMOUNT) {
    const discountValueInCents = Math.abs(dollarsToCents(discountValue));
    // Negative values are allowed, but the consumer of this method should handle them properly
    return totalCents - discountValueInCents;
  }

  if (discountType === OfferDiscountTypes.PERCENTAGE) {
    const discountPercentage = discountValue / 100;
    // If the discount percentage is over 100% the order is free...
    if (discountPercentage >= 1) {
      return 0;
    }
    const discountValueInCents = Math.round(totalCents * discountPercentage);
    return totalCents - discountValueInCents;
  }
  // there is a discountType we are not accounting for... Consider a log here to track this?
  return totalCents;
}

function calculateLoyaltyDiscounts(cartEntries: ICartEntry[], loyaltyInfo: ILoyaltyInfo) {
  const rewardsDiscount = getLoyaltyRewardsDiscount({
    cartEntriesForPricing: cartEntries,
    ...loyaltyInfo,
  });
  const offersDiscount = getLoyaltyOffersDiscount({
    cartEntriesForPricing: cartEntries,
    loyaltyInfo,
  });

  return rewardsDiscount + offersDiscount;
}

function getLoyaltyOffersDiscount({
  cartEntriesForPricing,
  loyaltyInfo: { loyaltyEnabled, offersEligibleItems, loyaltySelectedOffer, rewardTierApplied },
}: {
  cartEntriesForPricing: ICartEntryForPricing[];
  loyaltyInfo: ILoyaltyInfo;
}): number {
  if (!loyaltyEnabled || isUndefined(loyaltySelectedOffer) || isUndefined(offersEligibleItems)) {
    return 0;
  }

  // Exclude reward-tier eligible items from offers discount to avoid double-counting:
  // reward-tier discount is already applied in getLoyaltyRewardsDiscount via incentiveSelections.
  const rewardTierId = rewardTierApplied?.id;
  const eligibleItemsForOffersOnly = rewardTierId
    ? offersEligibleItems.filter(item => item.incentiveId !== rewardTierId)
    : offersEligibleItems;

  return eligibleItemsForOffersOnly.reduce((totalDiscount, eligibleItem) => {
    const discount = calculateEligibleItemDiscount(
      eligibleItem,
      loyaltySelectedOffer,
      cartEntriesForPricing
    );
    return totalDiscount + discount;
  }, 0);
}

function calculateEligibleItemDiscount(
  eligibleItem: any,
  loyaltySelectedOffer: any,
  cartEntriesForPricing: ICartEntryForPricing[]
): number {
  const isEligibleForOffer = eligibleItem?.incentiveId === loyaltySelectedOffer?.loyaltyEngineId;

  if (!isEligibleForOffer) {
    return 0;
  }

  const eligibleItemCartEntry = cartEntriesForPricing.find(
    cartEntry => cartEntry.cartId === eligibleItem.lineId
  );

  if (!eligibleItemCartEntry) {
    return eligibleItem.savingsAmount ?? 0;
  }

  return getItemOfferDiscount({
    cartEntry: eligibleItemCartEntry,
    offer: loyaltySelectedOffer,
    eligibleItem,
  });
}

function getLoyaltyRewardsDiscount({
  cartEntriesForPricing,
  loyaltyEnabled,
  rewardTierApplied,
  incentiveSelections,
}: {
  cartEntriesForPricing: ICartEntryForPricing[];
  loyaltyEnabled: boolean;
  rewardTierApplied?: { id: string } | null;
  incentiveSelections?: Array<{ incentiveId: string; lineId: string }>;
}): number {
  if (!loyaltyEnabled || !rewardTierApplied || !incentiveSelections?.length) {
    return 0;
  }

  const rewardAppliedLineIds = new Set(
    incentiveSelections
      .filter(incentive => incentive.incentiveId === rewardTierApplied.id)
      .map(incentive => incentive.lineId)
  );

  return cartEntriesForPricing.reduce((totalDiscount, cartEntry) => {
    if (!rewardAppliedLineIds.has(cartEntry.cartId)) {
      return totalDiscount;
    }
    const discount = getItemLoyaltyRewardDiscount({ cartEntry });
    return totalDiscount + discount;
  }, 0);
}
